import React, { useEffect, useState } from "react";
import { UserCircle, Plus, Trash2, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState } from "../components/ui";

const SECTIONS = [
  { key: "dependents", label: "Dependents & Family", fields: [["name", "Name"], ["relation", "Relation"], ["dob", "Date of Birth"]] },
  { key: "education", label: "Education", fields: [["degree", "Degree"], ["institution", "Institution"], ["year", "Year"]] },
  { key: "experience", label: "Work Experience", fields: [["company", "Company"], ["role", "Role"], ["years", "Years"]] },
  { key: "emergencyContacts", label: "Emergency Contacts", fields: [["name", "Name"], ["relation", "Relation"], ["phone", "Phone"]] },
];

export default function Profile360() {
  const { user } = useAuth();
  const eid = user?.employee?.id || user?.employeeId;
  const [profile, setProfile] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [crOpen, setCrOpen] = useState(false);
  const [cr, setCr] = useState({ field: "phone", label: "Phone", newValue: "" });
  const isAdmin = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => { if (eid) api.get(`/profile/detail/${eid}`).then(({ data }) => setProfile(data)); api.get("/profile/change-requests").then(({ data }) => setReqs(data)); };
  useEffect(() => { load(); }, [eid]);
  const addItem = async (e) => { e.preventDefault(); try { await api.post(`/profile/${eid}/section/${modal.section || modal.key}`, form); setModal(null); setForm({}); load(); } catch (err) { window.alert(apiError(err.response?.data?.detail)); } };
  const delItem = async (section, idx) => { await api.delete(`/profile/${eid}/section/${section}/${idx}`); load(); };
  const submitCr = async (e) => { e.preventDefault(); try { await api.post("/profile/change-request", cr); setCrOpen(false); setCr({ field: "phone", label: "Phone", newValue: "" }); load(); } catch (err) { window.alert(apiError(err.response?.data?.detail)); } };
  const decideCr = async (id, status) => { await api.put(`/profile/change-requests/${id}/decide`, { status }); load(); };
  if (eid && !profile) return <Loading />;

  return (
    <div>
      <PageHeader title="My Profile" subtitle="Personal details, family, education & change requests">
        <button data-testid="raise-change" onClick={() => setCrOpen(true)} className="btn-outline"><Send size={15} /> Request a Change</button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {SECTIONS.map((s) => (
          <Card key={s.key} className="p-5" data-testid={`section-${s.key}`}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">{s.label}</h3>
              <button data-testid={`add-${s.key}`} onClick={() => { setForm({}); setModal(s); }} className="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline"><Plus size={14} /> Add</button>
            </div>
            {(profile?.[s.key] || []).length === 0 ? <p className="text-sm text-slate-400">No records yet.</p> : (
              <div className="space-y-2">
                {profile[s.key].map((item, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/50" data-testid={`${s.key}-item-${i}`}>
                    <div className="text-sm text-slate-700 dark:text-slate-200">{s.fields.map(([k]) => item[k]).filter(Boolean).join(" · ")}</div>
                    <button onClick={() => delItem(s.key, i)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <h3 className="mb-3 mt-8 font-head text-lg font-bold text-slate-900 dark:text-white">{isAdmin ? "Data-Change Requests" : "My Change Requests"}</h3>
      {reqs.length === 0 ? <EmptyState icon={UserCircle} title="No change requests" /> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full" data-testid="cr-table">
            <thead><tr>{isAdmin && <th className="th">Employee</th>}<th className="th">Field</th><th className="th">Current</th><th className="th">Requested</th><th className="th">Status</th>{isAdmin && <th className="th">Action</th>}</tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {reqs.map((r) => (
                <tr key={r.id} data-testid={`cr-${r.id}`}>
                  {isAdmin && <td className="td text-sm font-medium">{r.employeeName}</td>}
                  <td className="td text-sm">{r.label}</td>
                  <td className="td text-sm text-slate-400">{String(r.oldValue ?? "—")}</td>
                  <td className="td text-sm font-medium">{String(r.newValue ?? "—")}</td>
                  <td className="td"><Badge value={r.status} /></td>
                  {isAdmin && <td className="td">{r.status === "PENDING" ? (
                    <div className="flex gap-1.5">
                      <button data-testid={`cr-approve-${r.id}`} onClick={() => decideCr(r.id, "APPROVED")} className="rounded bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15">Approve</button>
                      <button data-testid={`cr-reject-${r.id}`} onClick={() => decideCr(r.id, "REJECTED")} className="rounded bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-500/15">Reject</button>
                    </div>
                  ) : <span className="text-xs text-slate-400">—</span>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div></Card>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title={`Add — ${modal?.label || ""}`}
        footer={<><button onClick={() => setModal(null)} className="btn-outline">Cancel</button><button form="sec-form" data-testid="sec-submit" className="btn-accent">Add</button></>}>
        <form id="sec-form" onSubmit={addItem} className="space-y-3">
          {modal?.fields.map(([k, l]) => (
            <div key={k}><label className="label">{l}</label><input data-testid={`f-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="input" /></div>
          ))}
        </form>
      </Modal>

      <Modal open={crOpen} onClose={() => setCrOpen(false)} title="Request Profile Change"
        footer={<><button onClick={() => setCrOpen(false)} className="btn-outline">Cancel</button><button form="cr-form" data-testid="cr-submit" className="btn-accent">Submit</button></>}>
        <form id="cr-form" onSubmit={submitCr} className="space-y-3">
          <div><label className="label">Field</label>
            <select value={cr.field} onChange={(e) => setCr({ ...cr, field: e.target.value, label: e.target.options[e.target.selectedIndex].text })} className="input">
              <option value="phone">Phone</option><option value="personalEmail">Personal Email</option><option value="address">Address</option><option value="emergencyContact">Emergency Contact</option>
            </select>
          </div>
          <div><label className="label">New Value *</label><input required data-testid="cr-value" value={cr.newValue} onChange={(e) => setCr({ ...cr, newValue: e.target.value })} className="input" /></div>
        </form>
      </Modal>
    </div>
  );
}
